import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SpeakerWaveIcon,
  XMarkIcon,
  EyeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  FireIcon,
  BookmarkIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { socket, useSocket } from '../../utils/socket';
import AnnouncementService from '../../services/announcement.service';
import toast from '../../utils/toast';
import { formatDateDisplay } from '../../utils/dateDisplay';

const AnnouncementSpeaker = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewAnnouncement, setHasNewAnnouncement] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Fetch announcements when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchAnnouncements();
    }
  }, [isOpen]);

  // Socket.IO listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleNewAnnouncement = (announcement) => {
      console.log('📢 New announcement received:', announcement);
      setAnnouncements(prev => [announcement, ...prev]);
      setUnreadCount(prev => prev + 1);
      setHasNewAnnouncement(true);
      
      // Show toast notification
      toast.success(`New announcement: ${announcement.title}`, {
        duration: 5000
      });

      // Auto-hide the new announcement indicator after 10 seconds
      setTimeout(() => setHasNewAnnouncement(false), 10000);
    };

    const handleAnnouncementUpdated = (announcement) => {
      console.log('📝 Announcement updated:', announcement);
      setAnnouncements(prev => 
        prev.map(a => a.id === announcement.id ? { ...a, ...announcement } : a)
      );
    };

    const handleAnnouncementDeleted = (data) => {
      console.log('🗑️ Announcement deleted:', data);
      setAnnouncements(prev => prev.filter(a => a.id !== data.id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    };

    socket.on('announcement:new', handleNewAnnouncement);
    socket.on('announcement:updated', handleAnnouncementUpdated);
    socket.on('announcement:deleted', handleAnnouncementDeleted);

    return () => {
      socket.off('announcement:new', handleNewAnnouncement);
      socket.off('announcement:updated', handleAnnouncementUpdated);
      socket.off('announcement:deleted', handleAnnouncementDeleted);
    };
  }, [navigate]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await AnnouncementService.getAnnouncements({
        filter: 'active',
        limit: 5
      });
      
      if (response && response.success) {
        const fetchedAnnouncements = response.data || [];
        setAnnouncements(fetchedAnnouncements);
        
        // Count unread announcements
        const unread = fetchedAnnouncements.filter(a => !a.isRead).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (announcementId, event) => {
    event.stopPropagation();
    try {
      await AnnouncementService.markAsRead(announcementId);
      
      // Update local state
      setAnnouncements(prev =>
        prev.map(a => 
          a.id === announcementId ? { ...a, isRead: true } : a
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking announcement as read:', error);
      toast.error('Failed to mark announcement as read');
    }
  };

  const handleAnnouncementClick = (announcement) => {
    // Mark as read if not already read
    if (!announcement.isRead) {
      handleMarkAsRead(announcement.id, { stopPropagation: () => {} });
    }
    
    // Navigate to announcements page
    navigate('/announcements');
    setIsOpen(false);
  };

  const getAnnouncementIcon = (type, priority) => {
    if (priority === 'critical') {
      return <FireIcon className="w-4 h-4 text-red-600" />;
    }
    
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
      default:
        return <InformationCircleIcon className="w-4 h-4 text-blue-600" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'border-l-red-500 bg-red-50';
      case 'high':
        return 'border-l-orange-500 bg-orange-50';
      case 'normal':
        return 'border-l-blue-500 bg-blue-50';
      case 'low':
        return 'border-l-gray-500 bg-gray-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateDisplay(date);
  };

  // Show only active, non-expired announcements
  const displayAnnouncements = announcements.filter(a => 
    a.isActive && !a.isExpired && !a.isScheduled
  ).slice(0, 5);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Speaker Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200"
      >
        <SpeakerWaveIcon className="w-6 h-6" />
        
        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-medium"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}

        {/* New Announcement Pulse */}
        {hasNewAnnouncement && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1.2, opacity: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute -top-1 -right-1 w-6 h-6 bg-orange-400 rounded-full"
          />
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-12 w-80 sm:w-96 max-h-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-500 text-white">
              <div className="flex items-center gap-2">
                <SpeakerWaveIcon className="w-5 h-5" />
                <h3 className="font-semibold">Announcements</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-1 text-xs bg-white bg-opacity-20 text-white rounded-full font-medium">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-white hover:text-gray-200 rounded"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Announcements List */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading announcements...</p>
                </div>
              ) : displayAnnouncements.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {displayAnnouncements.map((announcement) => (
                    <motion.div
                      key={announcement.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`p-3 hover:bg-gray-50 transition-colors cursor-pointer group border-l-4 ${getPriorityColor(announcement.priority)} ${
                        !announcement.isRead ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleAnnouncementClick(announcement)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getAnnouncementIcon(announcement.type, announcement.priority)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {announcement.isPinned && (
                                  <BookmarkIcon className="w-3 h-3 text-orange-500" />
                                )}
                                <p className={`text-sm font-medium text-gray-800 line-clamp-1 ${
                                  !announcement.isRead ? 'font-semibold' : ''
                                }`}>
                                  {announcement.title}
                                </p>
                              </div>
                              <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                                {announcement.content}
                              </p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <ClockIcon className="w-3 h-3" />
                                  <span>{formatTime(announcement.createdAt)}</span>
                                </div>
                                {announcement.priority === 'critical' && (
                                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full font-medium">
                                    URGENT
                                  </span>
                                )}
                                {announcement.priority === 'high' && (
                                  <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full font-medium">
                                    HIGH
                                  </span>
                                )}
                              </div>
                            </div>
                            {!announcement.isRead && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                                <button
                                  onClick={(e) => handleMarkAsRead(announcement.id, e)}
                                  className="p-1 text-gray-400 hover:text-blue-600 rounded"
                                  title="Mark as read"
                                >
                                  <EyeIcon className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">
                  <SpeakerWaveIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm font-medium">No announcements</p>
                  <p className="text-xs mt-1">All caught up!</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  navigate('/announcements');
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-orange-600 hover:text-orange-800 font-medium py-2 hover:bg-orange-50 rounded transition-colors"
              >
                View all announcements
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnnouncementSpeaker;
